export const PRODUCT_COLLECTION_TYPE = "product-collection";

/** Link-in-bio public path: `/:username/collections/:unique_id`. */
export const PRODUCT_COLLECTION_PATH_SEGMENT = "collections";

/** Website public path: `/:username/shop/collections/:unique_id`. */
export const PRODUCT_COLLECTION_WEBSITE_PATH_SEGMENT = "shop/collections";

export function getProductCollectionRoute(
  publicId: string,
  forWebsite = false
) {
  const id = String(publicId || "").trim();
  if (!id) return "";
  return forWebsite
    ? `${PRODUCT_COLLECTION_WEBSITE_PATH_SEGMENT}/${id}`
    : `${PRODUCT_COLLECTION_PATH_SEGMENT}/${id}`;
}

export function isOpaqueComponentPublicId(value: string) {
  return /^[a-z0-9]+$/i.test(String(value || "").trim());
}

export function isProductCollectionType(component: any) {
  const type =
    typeof component === "string"
      ? component
      : String(component?.type ?? "");
  return type.trim().toLowerCase() === PRODUCT_COLLECTION_TYPE;
}

export function excludeProductCollections<T = any>(
  rows: T[] | null | undefined
): T[] {
  if (!Array.isArray(rows)) return [];
  return rows.filter((row) => !isProductCollectionType(row));
}

export function parseProductIds(postIds: unknown): string[] {
  if (Array.isArray(postIds)) {
    return postIds.map((id) => String(id).trim()).filter(Boolean);
  }
  return String(postIds ?? "")
    .split(",")
    .map((id) => id.trim())
    .filter(Boolean);
}

export function serializeProductIds(ids: string[]): string {
  return ids.map((id) => String(id).trim()).filter(Boolean).join(",");
}

type CollectionDetails = {
  collection_date?: string;
};

export function parseCollectionDetails(details: unknown): CollectionDetails {
  const raw = String(details ?? "").trim();
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === "object") {
      return {
        collection_date: String(parsed.collection_date || "").trim() || undefined,
      };
    }
  } catch {
    if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
      return { collection_date: raw };
    }
  }
  return {};
}

export function serializeCollectionDetails(collectionDate?: string | null) {
  const date = String(collectionDate || "").trim();
  if (!date) return "";
  return JSON.stringify({ collection_date: date });
}

export function getCollectionDisplayDate(component: {
  details?: unknown;
  collection_date?: unknown;
}): string {
  const fromDetails = parseCollectionDetails(component?.details).collection_date;
  if (fromDetails) return fromDetails;
  return String(component?.collection_date || "").trim();
}

/** Display / picker format for collection dates (US). Storage stays YYYY-MM-DD. */
export const COLLECTION_DATE_DISPLAY_FORMAT = "MM/DD/YYYY";

export function formatCollectionDateForDisplay(isoDate?: string | null) {
  const raw = String(isoDate || "").trim();
  if (!raw) return "";
  const parsed = new Date(`${raw}T00:00:00`);
  if (Number.isNaN(parsed.getTime()) || parsed.getFullYear() <= 2000) return "";
  const month = String(parsed.getMonth() + 1).padStart(2, "0");
  const day = String(parsed.getDate()).padStart(2, "0");
  const year = String(parsed.getFullYear());
  return `${month}/${day}/${year}`;
}

export function isUsableProductDate(value: unknown) {
  if (!value) return false;
  const parsed = new Date(String(value));
  return !Number.isNaN(parsed.getTime()) && parsed.getFullYear() > 2000;
}

/** Product Bank / picker sort key: newest first. Unusable dates sort last. */
export function productBankSortTime(row: {
  post_date?: unknown;
  created_at?: unknown;
} | null | undefined) {
  const stamp = row?.post_date || row?.created_at;
  if (!isUsableProductDate(stamp)) return 0;
  return new Date(String(stamp)).getTime();
}

/** Insert `newId` among `ids` in latest-to-oldest order (same as the product picker). */
export function insertProductIdNewestFirst(
  ids: string[],
  newId: string,
  getRow: (id: string) => {
    post_date?: unknown;
    created_at?: unknown;
  } | null | undefined
) {
  if (ids.includes(newId)) return ids;
  const newTime = productBankSortTime(getRow(newId));
  const insertAt = ids.findIndex(
    (id) => productBankSortTime(getRow(id)) < newTime
  );
  if (insertAt === -1) return [...ids, newId];
  const next = ids.slice();
  next.splice(insertAt, 0, newId);
  return next;
}
